// @ts-ignore
import { buttonColors } from '@ghs/components';
import { GridRowModes } from '@mui/x-data-grid-pro';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { attach, combine, createEffect, createEvent, createStore, sample } from 'effector';
import { persist } from 'effector-storage/query';
import { isEmpty, keyBy, sortBy } from 'lodash';
import { debounce, delay, spread } from 'patronum';
import qs from 'qs';
import { pipe } from 'ramda';
import { createElement } from 'react';
import { $$campaignVersionsDialog } from '../../../components/CampaignVersionsDialog/model';
import { $$confirmationDialog } from '../../../components/ConfirmationDialog/model.js';
import { $$infoDialog } from '../../../components/InfoDialog/model.js';
import { $$snackbar } from '../../../components/SnackbarRoot/model.js';
import { $$reseller } from '../../../services/ResellerService/model.js';
import createDataGridModel from '../../../util/createDataGridModel.js';
import createFxStatuses from '../../../util/createFxStatuses.js';
import { treeShakeObject, VOID } from '../../../util/JsonUtils.js';
import CampaignDeleteConfirmationMessage from './CampaignDeleteConfirmationMessage.jsx';
import FailedCampaignUpdateDialogContent from './FailedCampaignUpdateDialogContent.jsx';
import { DEFAULT_MAIL_DATE_FILTER_ID, mailDateFilters, shouldValidateCampaignRow } from './utils.js';
import { $$app } from '../../../components/App/model.tsx';

/**
 * @typedef {import('@mui/x-data-grid-pro').GridRowId} GridRowId
 * @typedef {import('@mui/x-data-grid-pro').GridValidRowModel} GridValidRowModel
 * @typedef {{ id: Reseller.Campaign['id'], field: keyof Reseller.Campaign, error: string }} ValidationError
 */

const $filters = createStore(/** @type {{ search?: string; mailDate?: string; customerIds?: string; lineOfBusinessIds?: string }} */ ({ mailDate: DEFAULT_MAIL_DATE_FILTER_ID }));

const $customers = createStore(/** @type {Reseller.Customer[]} */ ([]));

const $linesOfBusiness = createStore(/** @type {Reseller.LineOfBusiness[]} */ ([]));

const filterUpdatedByAutoComplete = createEvent();

const filterUpdatedByUser = createEvent();

// Wrap the effects to be able to handle the done event in each page separately and avoid overlaps
const getLineOfBusinessFx = attach({ effect: $$reseller.getLineOfBusinessFx });

// Wrap the effects to be able to handle the done event in each page separately and avoid overlaps
const getCustomersFx = attach({ effect: $$reseller.getCustomersFx });

/** @type {import('effector').EventCallable<void>} */
const refetch = createEvent();

/** @type {import('effector').EventCallable<{ key: keyof (typeof $filters)['__']; value: unknown }>} */
const setFilterByKey = createEvent();

/** @type {import('effector').EventCallable<Reseller.Campaign['id']>} */
const deleteClicked = createEvent();

/** @type {import('effector').EventCallable<GridRowId>} */
const saveClicked = createEvent();

/** @type {import('effector').EventCallable<Reseller.Campaign['id']>} */
const cancelClicked = createEvent();

/** @type {import('effector').EventCallable<Reseller.Campaign['id']>} */
const eyeIconClicked = createEvent();

/** @type {import('effector').EventCallable<void>} */
const openUnsavedChangesWarning = createEvent();

/** @type {import('effector').EventCallable<GridRowId>} */
const secondConfirmationTriggered = createEvent();

/** @type {(startDateField: string, endDateField: string) => import('effector').Store<{ search?: string; customerIds?: string; lineOfBusinessIds?: string; }>} */
const generateFilters = (startDateField, endDateField) => {
  return $filters.map(({ mailDate, ...filters }) => {
    const mailDateFilter = mailDateFilters.find(f => f.id === (mailDate || DEFAULT_MAIL_DATE_FILTER_ID)) || null;
    if (!mailDateFilter) {
      return filters;
    }
    return {
      ...filters,
      [startDateField]: dayjs().startOf('day').add(mailDateFilter.mailDateLowerBoundDays, 'day').format('MM-DD-YYYY'),
      [endDateField]: dayjs().endOf('day').add(mailDateFilter.mailDateUpperBoundDays, 'day').format('MM-DD-YYYY')
    };
  });
};

/** @type {import('effector').Effect<Reseller.Campaign, boolean>} */
const validateCampaignCandidateFx = createEffect(async ({ customer, name, number, lineOfBusiness }) => {
  return $$reseller
    .getCampaignsFx({
      customersIds: customer.id,
      campaignNumber: number,
      campaignName: name,
      lineOfBusinessIds: lineOfBusiness.id
    })
    .then(result => result.numberOfElements === 0);
});

const getFreshRowsFx = attach({
  source: sample({
    source: {
      dates: generateFilters('mailDateStart', 'mailDateEnd'),
      filters: $filters.map(({ customerIds, lineOfBusinessIds }) => ({ customerIds, lineOfBusinessIds }))
    },
    fn: ({ dates, filters }) => treeShakeObject({ ...dates, ...filters })
  }),
  /** @type {(params: DataGrid.ServerSideParams, filters: (typeof $filters)['__']) => object} */
  mapParams: (pagination, filters) => treeShakeObject({ ...pagination, ...filters, persistResult: true }),
  effect: $$reseller.getCampaignsFx
});

const $$campaigns = createDataGridModel({
  type: 'server',
  effect: getFreshRowsFx,
  refetch
});

const $$validationStatuses = createFxStatuses(validateCampaignCandidateFx);

// Tracks pending dropdown requests so we don't refetch campaigns unnecessarily
const $pendingDropdownFetches = createStore(0)
  .on(getCustomersFx, n => n + 1)
  .on(getLineOfBusinessFx, n => n + 1)
  .on(getCustomersFx.finally, n => n - 1)
  .on(getLineOfBusinessFx.finally, n => n - 1);

// Flags encapsulates when we need to do a campaign refetch
const $needsRefetch = createStore(false)
  .on($filters.updates, () => true)
  .on(refetch, () => false);

// True from when filters change until campaigns finish loading.Fixed the gap between dropdown request finishing and campaign refetch starting.
const $isDropdownRefetchCycleInProgress = createStore(false)
  .on(
    sample({
      clock: $filters.updates,
      source: $$campaigns.__.getFreshRowsFx.pending,
      filter: pending => !pending
    }),
    () => true
  )
  .on($$campaigns.__.getFreshRowsFx.finally, () => false);

// Provides event for when a filter is changed that requires a customer list refetch
const customerRelevantFilterChanged = filterUpdatedByUser.filter({
  fn: ({ key }) => !['customerIds', 'lineOfBusinessIds'].includes(key)
});

// Provides event for when a filter is changed that requires a LOB list refetch (includes autocomplete)
const lobRelevantFilterChanged = sample({
  clock: [filterUpdatedByUser, filterUpdatedByAutoComplete],
  filter: ({ key }) => key !== 'lineOfBusinessIds'
});

// Wait 100ms after all dropdown fetches complete before its "settled" so that the auto-complete flow is done in time to start the next request
const dropdownFetchSettled = debounce({
  source: sample({
    clock: $pendingDropdownFetches.updates,
    filter: pending => pending === 0
  }),
  timeout: 100
});

const $isCampaignTableLoading = combine(
  $$campaigns.__.getFreshRowsFx.pending,
  $pendingDropdownFetches,
  $isDropdownRefetchCycleInProgress,
  (isFetching, pendingDropdowns, isDropdownRefetchCycleInProgress) => isFetching || pendingDropdowns > 0 || isDropdownRefetchCycleInProgress
);

persist({
  store: $filters,
  key: 'filters',
  serialize: pipe(treeShakeObject, qs.stringify),
  deserialize: qs.parse
});

// Ensures that when we change personas the filters are cleared
sample({
  clock: $$app.personaChanged,
  target: $filters.reinit
});

// Updates $filters and routes them to the correct event (user or auto-complete)
sample({
  source: $filters,
  clock: setFilterByKey,
  fn: (filters, { key, value, isAutocomplete }) => {
    let newFilters;

    if (key === 'customerIds') {
      newFilters = { ...filters, lineOfBusinessIds: null, [key]: value };
    } else {
      newFilters = { ...filters, [key]: value };
    }

    const result = { newFilters };

    if (isAutocomplete) {
      result.isAutoCompleteFilterUpdate = { key, value };
    } else {
      result.isFilterUpdate = { key, value };
    }

    return result;
  },
  target: spread({
    newFilters: $filters,
    isAutoCompleteFilterUpdate: filterUpdatedByAutoComplete,
    isFilterUpdate: filterUpdatedByUser
  })
});

// Done on mount so that we always fetch customer list to populate dropdown (even if customerIds is persisted as they could change)
sample({
  source: generateFilters('startDate', 'endDate'),
  clock: $$campaigns.tableMounted.filter({ fn: Boolean }),
  fn: filters => treeShakeObject({ ...filters }),
  target: getCustomersFx
});

// Done on customer relevant filter change so that we fetch customer list only when needed.
sample({
  source: generateFilters('startDate', 'endDate'),
  clock: customerRelevantFilterChanged,
  fn: filters => treeShakeObject({ ...filters }),
  target: getCustomersFx
});

// Sort customer alphabetically
sample({
  clock: getCustomersFx.doneData,
  fn: customers => sortBy(customers, [customer => customer.name.toLowerCase()]),
  target: $customers
});

// Auto-select customer if only 1 customer exists
sample({
  source: $filters,
  clock: $customers.updates,
  filter: (filters, customers) => customers.length === 1 && !filters.customerIds,
  fn: (_, customers) => {
    return {
      key: 'customerIds',
      value: customers[0].id,
      isAutocomplete: true
    };
  },
  target: setFilterByKey
});

// Clear customerIds if selected customer no longer exists in the list
sample({
  clock: getCustomersFx.doneData,
  source: $filters,
  filter: (filters, customers) => filters.customerIds && !customers.some(c => c.id === filters.customerIds),
  fn: () => ({
    key: 'customerIds',
    value: null,
    isAutocomplete: true
  }),
  target: setFilterByKey
});

// On mount we fetch LOB if customerIds is persisted
sample({
  source: {
    filters: generateFilters('startDate', 'endDate'),
    customerIds: $filters.map(filters => filters.customerIds || null)
  },
  clock: $$campaigns.tableMounted.filter({ fn: Boolean }),
  filter: ({ customerIds }) => Boolean(customerIds),
  fn: ({ filters, customerIds }) => treeShakeObject({ ...filters, customerIds }),
  target: getLineOfBusinessFx
});

// On relevant filter change we fetch lob list if customerIds is present
sample({
  source: {
    filters: generateFilters('startDate', 'endDate'),
    customerIds: $filters.map(filters => filters.customerIds || null)
  },
  clock: lobRelevantFilterChanged,
  filter: ({ customerIds }) => Boolean(customerIds),
  fn: ({ filters, customerIds }) => treeShakeObject({ ...filters, customerIds }),
  target: getLineOfBusinessFx
});

// Sort LOB alphabetically
sample({
  clock: getLineOfBusinessFx.doneData,
  fn: linesOfBusinesses => sortBy(linesOfBusinesses, [linesOfBusiness => linesOfBusiness.name.toLowerCase()]),
  target: $linesOfBusiness
});

// Auto-select LOB if only 1 LOB exists
sample({
  source: $filters,
  clock: $linesOfBusiness.updates,
  filter: (filters, linesOfBusinesses) => linesOfBusinesses.length === 1 && !filters.lineOfBusinessIds,
  fn: (_, linesOfBusinesses) => {
    return {
      key: 'lineOfBusinessIds',
      value: linesOfBusinesses[0].id,
      isAutocomplete: true
    };
  },
  target: setFilterByKey
});

// Clear lineOfBusinessIds if selected LOB no longer exists in the list
sample({
  clock: getLineOfBusinessFx.doneData,
  source: $filters,
  filter: (filters, lobs) => filters.lineOfBusinessIds && !lobs.some(lob => lob.id === filters.lineOfBusinessIds),
  fn: () => ({
    key: 'lineOfBusinessIds',
    value: null,
    isAutocomplete: true
  }),
  target: setFilterByKey
});

// Clear LOB store when customerIds is set to null
sample({
  clock: $filters.map(filters => filters.customerIds === null).updates.filter({ fn: Boolean }),
  target: $linesOfBusiness.reinit
});

// Refetch campaigns when:
// 1. Filters are changed
// 2. All dropdown fetches settled (auto-complete flow has finished)
// 3. A campaign was deleted
// Filters: only if filters actually changed ($needsRefetch) and no dropdown request are pending (pending === 0)
sample({
  clock: [
    debounce({
      source: $filters.updates,
      timeout: 1000
    }),
    dropdownFetchSettled,
    $$reseller.deleteCampaignsFx.done
  ],
  source: { needsRefetch: $needsRefetch, pending: $pendingDropdownFetches },
  filter: ({ needsRefetch, pending }) => needsRefetch && pending === 0,
  target: refetch
});

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

sample({
  clock: delay(secondConfirmationTriggered, 0), // Delay is needed to let the confirmation dialog close before opening it again
  fn: id => ({
    confirmation: {
      title: 'Success!',
      content: 'All your changes have been successfully applied.',
      acceptButtonText: 'Save and exit',
      closeButtonText: 'Undo Changes',
      onAccept: () => {
        $$reseller.deleteCampaignsFx(id);
      }
    },
    highlight: [id]
  }),
  target: spread({ confirmation: $$confirmationDialog.open, highlight: $$campaigns.highlightRows })
});

sample({
  source: $$campaigns.$data,
  clock: deleteClicked,
  fn: (campaigns, id) => ({
    title: 'Campaign deletion confirmation',
    // @ts-ignore
    content: createElement(CampaignDeleteConfirmationMessage, { campaign: campaigns.find(c => c.id === id) }),
    onAccept: () => secondConfirmationTriggered(id),
    acceptButtonText: 'Delete',
    acceptButtonColor: buttonColors.ERROR
  }),
  target: $$confirmationDialog.open
});

sample({
  clock: $$reseller.deleteCampaignsFx.done,
  fn: ({ params: id }) => ({
    snackbar: { message: 'Campaign was deleted successfully', severity: /** @type {const} */ ('success') },
    resetRowChanges: id
  }),
  target: spread({
    snackbar: $$snackbar.open,
    resetRowChanges: $$campaigns.resetRowChanges
  })
});

sample({
  source: $$campaigns.$data,
  clock: $$reseller.deleteCampaignsFx.fail,
  fn: (campaigns, { params }) => {
    const campaign = campaigns.find(c => c.id === params);
    return {
      message: campaign ? `Campaign '${campaign.name} - ${campaign.number}' deleted` : 'Unexpected error',
      severity: /** @type {const} */ ('error')
    };
  },
  target: $$snackbar.open
});

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

sample({
  source: $$campaigns.$updatedRows,
  clock: saveClicked,
  filter: (updatedRows, id) => Boolean(keyBy(updatedRows, 'id')[id]),
  fn: (updatedRows, id) => keyBy(updatedRows, 'id')[id],
  target: $$reseller.updateCampaignsFx
});

sample({
  source: $$campaigns.$data,
  clock: $$reseller.updateCampaignsFx.done,
  fn: (data, { params: campaign }) => ({
    snackbarOpen: {
      message: `Campaign '${campaign.name} - ${campaign.number}' updated`,
      severity: /** @type {const} */ ('success')
    },
    setRowMode: { id: campaign.id, mode: GridRowModes.View },
    campaignIdToReset: campaign.id,
    updateData: data.map(row => (row.id === campaign.id ? campaign : row))
  }),
  target: spread({
    snackbarOpen: $$snackbar.open,
    setRowMode: $$campaigns.setRowMode,
    campaignIdToReset: $$campaigns.resetRowChanges,
    updateData: $$campaigns.$data
  })
});

sample({
  clock: $$reseller.updateCampaignsFx.fail,
  fn: ({ params: campaign, error }) => ({
    title: 'Campaign not updated',
    content: (
      <FailedCampaignUpdateDialogContent
        content={
          error instanceof AxiosError && typeof error.response?.data.message === 'string'
            ? error.response.data.message
            : `Error updating campaign '${campaign.name} - ${campaign.number}'. Please contact support.`
        }
      />
    )
  }),
  target: $$infoDialog.open
});

sample({
  source: {
    updatedRows: $$campaigns.$updatedRows,
    originalRows: $$campaigns.$data // grabbing unedited data
  },
  clock: debounce($$campaigns.cellValueChanged, 100),
  fn: ({ updatedRows, originalRows }, { id }) => {
    const updatedRow = /** @type {Reseller.Campaign[]} */ (updatedRows).find(r => r.id === id);
    const originalRow = /** @type {Reseller.Campaign[]} */ (originalRows).find(r => r.id === id);
    return updatedRow
      ? treeShakeObject({
          addCellError: [
            isEmpty(updatedRow?.name) && { id, field: /** @type {const} */ ('name'), error: 'Name is required' },
            isEmpty(updatedRow?.number) && { id, field: /** @type {const} */ ('number'), error: 'Number is required' }
          ].filter(Boolean),
          resetCellError: [
            !isEmpty(updatedRow?.name) && {
              id,
              field: /** @type {const} */ ('name')
            },
            !isEmpty(updatedRow?.number) && { id, field: /** @type {const} */ ('number') }
          ].find(Boolean),
          rowToValidate: shouldValidateCampaignRow(originalRow, updatedRow)
        })
      : {};
  },
  target: spread({
    rowToValidate: validateCampaignCandidateFx,
    addCellError: $$campaigns.addCellError,
    resetCellError: $$campaigns.resetCellError
  })
});

sample({
  clock: validateCampaignCandidateFx.done,
  fn: ({ params, result }) =>
    result
      ? { resetCellError: { id: params.id, field: params.field } }
      : {
          setErrors: [
            {
              id: params.id,
              field: /** @type {const} */ ('name'),
              error: 'Campaign with the same name and number already exists'
            },
            {
              id: params.id,
              field: /** @type {const} */ ('number'),
              error: 'Campaign with the same name and number already exists'
            }
          ]
        },
  target: spread({ setErrors: $$campaigns.addCellError, resetCellError: $$campaigns.resetCellError })
});

// When filters are changed, check if there are unsaved changes and reset row modes to View
sample({
  source: $$campaigns.$unsavedChanges,
  clock: [filterUpdatedByAutoComplete, filterUpdatedByUser],
  filter: unsavedChanges => unsavedChanges.length === 0,
  fn: () => GridRowModes.View,
  target: $$campaigns.setAllRowsMode
});

sample({
  clock: cancelClicked,
  fn: id => ({ setRowMode: { id, mode: GridRowModes.View }, resetUnsavedChanges: id }),
  target: spread({ setRowMode: $$campaigns.setRowMode, resetUnsavedChanges: $$campaigns.resetRowChanges })
});

sample({
  clock: openUnsavedChangesWarning,
  fn: () => ({
    title: 'Unsaved changes',
    content: 'You have unsaved changes. Please save or discard them before leaving the page.',
    buttons: [{ children: 'Cancel', onClick: () => $$confirmationDialog.close() }]
  }),
  target: $$confirmationDialog.open
});

const $hasUnsavedChanges = $$campaigns.$unsavedChanges.map(changes => changes.length > 0);

sample({
  source: $hasUnsavedChanges,
  clock: eyeIconClicked,
  fn: (hasUnsavedChanges, campaignId) => (hasUnsavedChanges ? { openUnsavedChangesWarning: VOID } : { openVersionsDialog: { campaignId } }),
  target: spread({
    openUnsavedChangesWarning: openUnsavedChangesWarning,
    openVersionsDialog: $$campaignVersionsDialog.open
  })
});

// @ts-ignore
window?.navigation?.addEventListener('navigate', event => {
  if ($hasUnsavedChanges.getState()) {
    openUnsavedChangesWarning();
    event.preventDefault();
  }
});

window.onbeforeunload = function () {
  if ($hasUnsavedChanges.getState()) {
    return true;
  }
};

export const $$adminCampaignsPage = {
  $$campaigns,
  $isCampaignTableLoading,
  $$validationStatuses,

  $filters: $filters.map(filters => ({ ...filters })),
  $linesOfBusiness: $linesOfBusiness.map(d => d),
  $customers: $customers.map(d => d),

  setFilterByKey,
  deleteClicked,
  saveClicked,
  cancelClicked,
  eyeIconClicked
};
